import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Painting', 'Kitchen & Bath', 'Flooring', 'Roofing', 'Windows & Doors',
  'Landscaping', 'HVAC', 'Electrical', 'Plumbing', 'General Contracting', 'Other',
];

const RELATIONSHIPS = [
  'Hired them for a project', 'Family member used them', 'Neighbour or friend recommended and I verified their work', 'Other direct experience',
];

export const ContractorReferralForm = ({ onSubmitted }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    contractor_name: '', contact_name: '', contractor_email: '', contractor_phone: '',
    website_or_social: '', service_category: '', city: '', service_area: '',
    work_description: '', approximate_service_date: '', recommendation_reason: '',
    homeowner_relationship: '', acknowledgement_confirmed: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const valid = form.contractor_name && form.contact_name && form.contractor_email &&
    form.contractor_phone && form.service_category && form.city && form.work_description &&
    form.approximate_service_date && form.recommendation_reason && form.homeowner_relationship &&
    form.acknowledgement_confirmed;

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/referral-program/contractor-referrals`, form);
      toast.success(res.data.message);
      onSubmitted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contractor-referral-form" className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="cr-name">Contractor or business name</Label>
          <Input id="cr-name" data-testid="cr-business-name" value={form.contractor_name} onChange={(e) => set('contractor_name', e.target.value)} className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="cr-contact">Contact person</Label>
          <Input id="cr-contact" data-testid="cr-contact-name" value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="cr-email">Email</Label>
          <Input id="cr-email" data-testid="cr-email" type="email" value={form.contractor_email} onChange={(e) => set('contractor_email', e.target.value)} className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="cr-phone">Phone number</Label>
          <Input id="cr-phone" data-testid="cr-phone" type="tel" value={form.contractor_phone} onChange={(e) => set('contractor_phone', e.target.value)} className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="cr-web">Website or social media <span className="text-neutral-400">(optional)</span></Label>
          <Input id="cr-web" data-testid="cr-website" value={form.website_or_social} onChange={(e) => set('website_or_social', e.target.value)} className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label>Service category</Label>
          <Select value={form.service_category} onValueChange={(v) => set('service_category', v)}>
            <SelectTrigger data-testid="cr-category" className="mt-1 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="cr-city">City or service area</Label>
          <Input id="cr-city" data-testid="cr-city" value={form.city} onChange={(e) => set('city', e.target.value)} className="mt-1 rounded-xl" />
        </div>
        <div>
          <Label htmlFor="cr-date">Approximate date you used the contractor</Label>
          <Input id="cr-date" data-testid="cr-date" placeholder="e.g. March 2026" value={form.approximate_service_date} onChange={(e) => set('approximate_service_date', e.target.value)} className="mt-1 rounded-xl" />
        </div>
      </div>
      <div>
        <Label htmlFor="cr-work">Description of the work completed</Label>
        <Textarea id="cr-work" data-testid="cr-work-description" rows={2} value={form.work_description} onChange={(e) => set('work_description', e.target.value)} className="mt-1 rounded-xl" />
      </div>
      <div>
        <Label htmlFor="cr-why">Why do you recommend them?</Label>
        <Textarea id="cr-why" data-testid="cr-reason" rows={2} value={form.recommendation_reason} onChange={(e) => set('recommendation_reason', e.target.value)} className="mt-1 rounded-xl" />
      </div>
      <div>
        <Label>Your relationship to the contractor</Label>
        <Select value={form.homeowner_relationship} onValueChange={(v) => set('homeowner_relationship', v)}>
          <SelectTrigger data-testid="cr-relationship" className="mt-1 rounded-xl"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <Checkbox data-testid="cr-acknowledgement" checked={form.acknowledgement_confirmed} onCheckedChange={(v) => set('acknowledgement_confirmed', !!v)} className="mt-0.5" />
        <span className="text-sm text-neutral-700">
          I confirm that I have personally used or directly experienced this contractor's work and would recommend their workmanship.
        </span>
      </label>
      <Button data-testid="cr-submit-btn" disabled={!valid || loading} onClick={submit} className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium">
        {loading ? 'Submitting...' : 'Submit Recommendation'}
      </Button>
      <p className="text-xs text-neutral-400">
        The contractor must be a legitimate home-service business, must not already be registered or previously rewarded through another referral, and must complete Spinser's required verification. Their information is never displayed publicly without consent.
      </p>
    </div>
  );
};
